import { Utils } from '../helpers/utils';
import { Filters } from '../helpers/filters';

export type QMenuOption = {
	label: string,
	value: string,
	selected: boolean
};
export type QQChild = {
	selection: string|string[],
	question_id: number,
	msgToPrepend: string,

	// in case of 'multiselect' or 'multimenu'
	and_or: 'and'|'or'
};

export class QuestionnaireQuestion {
	id: number;
	type_s: 'rate0-5'|'rate0-10'
				|'text'|'string'
				|'yes-no'|'yes-no-maybe'
				|'menu'|'multimenu'
				|'select'|'multiselect';

	local_id: number;

	title: string;
	answer: string;
	children: QQChild[] = [];
	optionsText: string[] = [];

	// in case it's a select, menu, multimenu or multiselect
	selectOptions: QMenuOption[] = [];
	// in case it's a multiselect or multimenu
	toStringFunc: (result: any[]) => string;
	separator: string = ";";
	minSelectedItems: number;
	maxSelectedItems: number;
	instructions: string;

	json_info: string; // json representation of question

	public static fromJsonInfo(question: QuestionnaireQuestion) {
		let info = JSON.parse(question.json_info);

		for(let key in info) {
			question[key] = info[key];
		}
	}

	public static prepareToSend(question: QuestionnaireQuestion) {
		if(question.children == null) {
			question.children = [];
		}

		if(question.type_s.indexOf("multi")) {
			for(let child of question.children) {
				if(child.and_or == "and" && typeof child.selection == "object") {
					child.selection = child.selection.join(";");
				}
			}
		}
	}
	public static setJsonInfo(question: QuestionnaireQuestion) {
		let keys = ['children','optionsText','selectOptions','separator', 'minSelectedItems', 'maxSelectedItems'];

		let json = {};
		for(let key of keys) {
			json[key] = question[key];
		}

		question.json_info = JSON.stringify(json);
	}

	public static getNext(question: QuestionnaireQuestion, questions: QuestionnaireQuestion[]): QuestionnaireQuestion {
		if(question.children == null || question.children.length == 0)
			return null;

		// if there is only 1 child, select it already.
		if(question.children.length == 1) {
			return Utils.findById(question.children[0].question_id, questions, 'local_id');
		}

		// can't have more than 1 jump on subjective questions
		if(question.type_s == "text" || question.type_s == "string") {
			if(question.children.length == 0)
				return null;

			if(question.children.length > 1) {
				console.error(QuestionnaireQuestion._errorTag(question), "subjective answers can't have more than 1 jump! First will always be selected! question id: ${question.id}, children count: ${question.children.length}");
			}

			return Utils.findById(question.children[0].question_id, questions, 'local_id');
		}

		for(let i = 0; i < question.children.length; i++) {
			let child = question.children[i];

			let match = false;
			if(typeof child.selection == "string") {
				match = question.answer == child.selection;
			} else { // array of strings. multiselect, multimenu or rate
				if(child.and_or) { // multiselect or multimenu
					if(child.and_or == "or") {
						for(let sel_str of child.selection) {
							if(match)
								break;

							for(let ans_str of question.answer.split(";")) {
								if(ans_str == sel_str) {
									match = true;

									break;
								}
							}
						}
					} else {
						let sel_str = child.selection.join(";");
						if(sel_str == question.answer)
							match = true;
					}
				} else { // rate
					if(child.selection.indexOf(question.answer) > -1) {
						match = true;
					}
				}
			}
			if(match){
				let found: QuestionnaireQuestion = Utils.findById(question.children[i].question_id, questions, 'local_id');
				if(found != null && !!child.msgToPrepend)
					found.title = child.msgToPrepend + "\n" + found.title;

				return found;
			}
		}

		return null;
	}

	public static init(question: QuestionnaireQuestion, minChildren: number = 1): void {
		if(!!question.json_info)
			QuestionnaireQuestion.fromJsonInfo(question);

		QuestionnaireQuestion._checkChildren(question, minChildren);
		question.optionsText = question.optionsText || [];

		switch(question.type_s) {
			case "yes-no-maybe": {
				QuestionnaireQuestion._checkChildren(question, 3);
			}
			case "yes-no": {
				QuestionnaireQuestion._checkChildren(question, 2);
				QuestionnaireQuestion._initButtons(question);
				break;
			}
			case "multiselect":
			case "multimenu": {
				QuestionnaireQuestion._initMulti(question);
				break;
			}
		}
	}

	private static _initButtons(question: QuestionnaireQuestion) {
		if(question.optionsText == null || question.optionsText.length == 0) {
			if(question.type_s == 'yes-no') 
				question.optionsText = ['SIM', 'NÃO'];
			else if(question.type_s == 'yes-no-maybe') {
				question.optionsText = ['SIM', 'TALVEZ', 'NÃO'];
			}
		}
	}

	private static _checkChildren(question: QuestionnaireQuestion, min: number) {
		if(question.children == null) {
			question.children = [];
		}
		for(let i = question.children.length; i < min; i++) {
			question.children.push(<QQChild>{
				selection: '',
				question_id: -1,
				msgToPrepend: ''
			});
		}
	}

	private static _initMulti(question: QuestionnaireQuestion) {
		QuestionnaireQuestion._checkMultiOptions(question);

		question.minSelectedItems = question.minSelectedItems || 1;
		if(question.maxSelectedItems == null || question.maxSelectedItems > question.selectOptions.length) {
			question.maxSelectedItems = question.selectOptions.length;
		}

		QuestionnaireQuestion._setMultiInstructions(question);

		question.separator = question.separator || ";";

		if(question.toStringFunc == null) {
			question.toStringFunc = (result: string[]) : string => {
				return Filters.orderAlphabetically(result).join(question.separator);
			}
		}

		QuestionnaireQuestion._orderChildren(question);
	}

	private static _checkMultiOptions(question: QuestionnaireQuestion) {
		if(question.selectOptions == null) {
			question.selectOptions = [];
		}
		for(let option of question.selectOptions) {
			if(option.value == null || option.value == "") {
				if(option.label == null || option.label == "") {
					console.error(QuestionnaireQuestion._errorTag(question), "option should have either 'label' and/or 'value'");
				} else {
					option.value = option.label;
				}
			} else {
				option.label = option.label || option.value;
			}
		}
	}

	private static _setMultiInstructions(question: QuestionnaireQuestion) {
		let msg = "Por favor, selecione ";
		let min = question.minSelectedItems.toString();
		let max = question.maxSelectedItems.toString();

		if(min > max) {
			console.error(QuestionnaireQuestion._errorTag(question), "minSelectedItems should be lesser than maxSelectedItems. Swapping now...");
			let aux = question.minSelectedItems;
			question.minSelectedItems = question.maxSelectedItems;
			question.maxSelectedItems = aux;

			let aux2 = min;
			min = max;
			max = aux2;
		}

		if(min == max) {
			msg += question.minSelectedItems.toString() + " opções.";
		} else {
			if(min == "1") {
				msg += "até " + max + " opções.";
			} else if(min > "1" && max == question.selectOptions.length.toString()) {
				msg += "pelo menos " + min + " opções.";
			} else {
				msg += "de " + min + " a " + max + " opções.";
			}
		}

		question.instructions = question.instructions || msg;
	}

	private static _orderChildren(question: QuestionnaireQuestion) {
		let to_order: {priority: string, child: QQChild}[] = [];

		for(let i = 0; i < question.children.length; i++) {
			let child = question.children[i];

			if(!!child.selection) {
				if(child.and_or == "and") {
					to_order.push({
						child: child,
						priority: (1000 + child.selection.length).toString()
					});
				} else if(child.and_or == "or") {
					if(typeof child.selection == "string") {
						child.selection = [child.selection];
					}
					to_order.push({
						child: child,
						priority: (100 - child.selection.length).toString()
					});
				}
			} else {
				to_order.push({
					child: child,
					priority: "0"
				});
			}
		}

		to_order = Filters.orderAlphabetically(to_order, "priority", true);

		let aux = [];
		for(let item of to_order) {
			aux.unshift(item.child);
		}
		question.children = aux;
	}

	private static _errorTag(question: QuestionnaireQuestion) {
		return `Question #${question.id}: `;
	}
}